import { makeStyles, mergeClasses } from '@fluentui/react-components';
import type { Presence } from '../lib/status';
import { vt } from '../theme';

/** The map adds "idle" (handshook recently, not online now) to the shared presence. */
export type DotState = Presence | 'idle';

const useStyles = makeStyles({
  dot: { display: 'block', width: '7px', height: '7px', borderRadius: '50%', backgroundColor: vt.fg4, flexShrink: 0 },
  online: { backgroundColor: vt.green },
  idle: { backgroundColor: vt.fg3 },
  // Mockups 01-03: a live device is a cyan dot in a soft cyan ring (tintCyan is too faint at 3px).
  live: { backgroundColor: vt.cyan, boxShadow: `0 0 0 3px color-mix(in srgb, ${vt.cyan} 30%, transparent)` },
  offline: {},
});

const WORD: Record<DotState, string> = { live: 'Live traffic', online: 'Online', idle: 'Idle', offline: 'Offline' };

interface Props {
  /** null = the statuses are not loaded yet: a grey dot that says so. */
  state: DotState | null;
  className?: string;
  /** Inside a control that already names the state (a map card): hidden from assistive tech. */
  decorative?: boolean;
}

/** One device's dot, on every screen that lists devices: green online, cyan with a halo live, grey offline. */
export function PresenceDot({ state, className, decorative = false }: Props) {
  const s = useStyles();
  return (
    <span
      className={mergeClasses(s.dot, state ? s[state] : undefined, className)}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': state ? WORD[state] : 'Status not loaded' })}
      data-presence={state ?? undefined}
    />
  );
}
